import once from 'lodash/once';
import { type CSSProperties, type ReactNode, createContext, useContext } from 'react';

import useControlled from '@/hooks/useControlled';
import { cn } from '@/lib/utils';

export type LegendContextProps<T> = {
  disabledSeriesValues?: T[];
  noToggleVisibility: boolean;
  onToggleSeries: (value: T, selected: boolean) => void;
};

type Props<T> = {
  className?: string;
  style?: CSSProperties;
  children: ReactNode;
  noToggleVisibility?: boolean;
  defaultDisabledValues?: T[];
  disabledValues?: T[];
  onSeriesToggle?: (value: T, selected: boolean) => void;
  onDisabledSeriesChange?: (disabledSeriesValues?: T[]) => void;
};

const createLegendContext = once(<T,>() => createContext({} as LegendContextProps<T>));

export const useLegendContext = <T extends string>() => {
  const context = useContext(createLegendContext<T>());
  if (!context) {
    throw new Error('useLegendContext must be used within a LegendProvider');
  }
  return context;
};

const Legend = <T extends string>({
  className = '',
  style = {},
  children,
  noToggleVisibility = false,
  defaultDisabledValues = [],
  disabledValues: disabledValuesProp,
  onSeriesToggle,
  onDisabledSeriesChange,
  ...rest
}: Props<T>) => {
  const [disabledValues, setDisabledValues] = useControlled(
    disabledValuesProp,
    defaultDisabledValues,
    onDisabledSeriesChange,
  );
  const LegendContext = createLegendContext<T>();

  const handleDisabledSeriesChange = (disabledSeriesValues: T[]) => {
    setDisabledValues(disabledSeriesValues);
    onDisabledSeriesChange?.(disabledSeriesValues);
  };

  const handleSeriesToggle = (value: T, selected: boolean) => {
    onSeriesToggle?.(value, selected);
    if (selected) {
      handleDisabledSeriesChange(disabledValues?.filter((v) => v !== value) ?? []);
    } else {
      handleDisabledSeriesChange([...(disabledValues ?? []), value]);
    }
  };

  return (
    <div
      className={cn(
        'flex min-h-14 flex-wrap items-center gap-x-3 gap-y-1.5 border-b border-gray-200 bg-gray-100 py-1.5 px-6',
        className,
      )}
      style={style}
      {...rest}
    >
      <LegendContext.Provider
        value={{
          disabledSeriesValues: disabledValues,
          noToggleVisibility,
          onToggleSeries: handleSeriesToggle,
        }}
      >
        {children}
      </LegendContext.Provider>
    </div>
  );
};

export default Legend;
